package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.people.PersonListEntry
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import kotlinx.browser.document
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLInputElement

private val tokenInput = Bem("admin-token-input")
private val optionList = Bem("admin-token-options")
private val field = Bem("admin-field")

/** A choice of an [AdminTokenInput], e.g. a person with [label] as the name. */
data class TokenOption<K : Any>(val key: K, val label: String)

/** How many matches the list offers at once; typing more narrows it. */
private const val MAX_MATCHES = 8

/** Where the focus goes once the field shows what a pick or a removal changed. */
private enum class PendingFocus { REMOVE, SEARCH }

/**
 * Picks several records by name, such as the people playing a role: the chosen ones as tokens, then a search field
 * that offers the matching [options] in a list (an ARIA combobox). ↑/↓ choose, Enter adds, Escape closes the list,
 * Backspace in the empty field removes the last token. [options] is `null` while they load. [label] names the field
 * for screen readers, e.g. "Personen für Regie"; [labelledBy] names it by an element instead.
 *
 * [single] picks one record: while one is chosen it is the only token and the search is hidden; removing it with × or
 * Backspace shows the search again with the focus in it. While [options] load, the search is disabled.
 */
@Composable
fun <K : Any> AdminTokenInput(
    id: String,
    label: String,
    selected: List<TokenOption<K>>,
    options: List<TokenOption<K>>?,
    onAdd: (K) -> Unit,
    onRemove: (K) -> Unit,
    placeholder: String = "Suchen …",
    noMatches: String = "Nichts gefunden.",
    invalid: Boolean = false,
    single: Boolean = false,
    disabled: Boolean = false,
    labelledBy: String? = null,
    describedBy: String? = null,
) {
    var query by remember { mutableStateOf("") }
    var open by remember { mutableStateOf(false) }
    var active by remember { mutableStateOf(0) }
    val listId = "$id-options"
    var field by remember { mutableStateOf<HTMLInputElement?>(null) }
    var removeButton by remember { mutableStateOf<HTMLButtonElement?>(null) }
    var pendingFocus by remember { mutableStateOf<PendingFocus?>(null) }
    val chosen = single && selected.isNotEmpty()
    val loading = single && options == null

    val selectedKeys = selected.map { it.key }.toSet()
    val words = query.trim().lowercase().split(Regex("\\s+")).filter { it.isNotEmpty() }
    val matches = options.orEmpty()
        .filter { it.key !in selectedKeys && words.all { word -> word in it.label.lowercase() } }
        .take(MAX_MATCHES)
    val activeIndex = active.coerceIn(0, (matches.size - 1).coerceAtLeast(0))
    val expanded = open && query.isNotBlank()

    // once the pick or removal shows: the search hides while a record is chosen, which would drop the focus
    SideEffect {
        // a record chosen from outside, e.g. by discarding changes, leaves no search behind for when it is removed
        if (chosen && query.isNotEmpty()) {
            query = ""
            open = false
        }
        when (pendingFocus) {
            PendingFocus.REMOVE -> if (chosen) {
                removeButton?.focus()
                pendingFocus = null
            }

            // a disabled search, e.g. while the records load, takes the focus once it is enabled, unless it went
            // elsewhere meanwhile
            PendingFocus.SEARCH -> when {
                chosen -> Unit
                document.activeElement.let { it != null && it != document.body } -> pendingFocus = null
                field?.matches(":disabled") == false -> {
                    field?.focus()
                    pendingFocus = null
                }
            }

            null -> Unit
        }
    }

    fun remove(key: K) {
        onRemove(key)
        if (single) {
            pendingFocus = PendingFocus.SEARCH
        }
    }

    fun add(option: TokenOption<K>) {
        onAdd(option.key)
        if (single) {
            pendingFocus = PendingFocus.REMOVE
        }
        query = ""
        // at once, so a key typed before the next recomposition starts a new search
        field?.value = ""
        active = 0
    }

    Div({
        classes(tokenInput.block)
        if (invalid) {
            attr("aria-invalid", "true")
        }
    }) {
        selected.forEach { token ->
            key(token.key) {
                Span({ classes(tokenInput.element("token").className) }) {
                    Text(token.label)
                    Button({
                        type(ButtonType.Button)
                        classes(tokenInput.element("remove").className)
                        attr("aria-label", "${token.label} entfernen")
                        if (single) {
                            ref {
                                removeButton = it
                                onDispose { removeButton = null }
                            }
                            onKeyDown { event ->
                                if (event.key == "Backspace") {
                                    event.preventDefault()
                                    remove(token.key)
                                }
                            }
                        }
                        onClick { remove(token.key) }
                    }) { Span({ attr("aria-hidden", "true") }) { Text("×") } }
                }
            }
        }

        Input(InputType.Text) {
            classes(tokenInput.element("search").className)
            id(id)
            value(query)
            placeholder(if (loading) "Lade …" else placeholder)
            attr("role", "combobox")
            if (labelledBy != null) {
                attr("aria-labelledby", labelledBy)
            } else {
                attr("aria-label", label)
            }
            describedBy?.let { attr("aria-describedby", it) }
            if (invalid) {
                attr("aria-invalid", "true")
            }
            if (chosen) {
                attr("hidden", "")
            }
            if (loading || disabled) {
                attr("disabled", "")
            }
            attr("aria-autocomplete", "list")
            attr("aria-controls", listId)
            attr("aria-expanded", expanded.toString())
            attr("autocomplete", "off")
            ref {
                field = it
                onDispose { field = null }
            }
            if (expanded && matches.isNotEmpty()) {
                attr("aria-activedescendant", "$listId-$activeIndex")
            }
            onInput {
                query = it.value
                open = true
                active = 0
            }
            onFocus { open = true }
            onBlur { open = false }
            onKeyDown { event ->
                when (event.key) {
                    "ArrowDown" -> if (expanded) {
                        event.preventDefault()
                        active = (activeIndex + 1).coerceAtMost(matches.size - 1)
                    }

                    "ArrowUp" -> if (expanded) {
                        event.preventDefault()
                        active = (activeIndex - 1).coerceAtLeast(0)
                    }

                    // never submit the surrounding form from here
                    "Enter" -> {
                        event.preventDefault()
                        if (expanded) {
                            matches.getOrNull(activeIndex)?.let(::add)
                        }
                    }

                    "Escape" -> if (expanded) {
                        event.preventDefault()
                        open = false
                    }

                    "Backspace" -> if (query.isEmpty()) {
                        selected.lastOrNull()?.let { remove(it.key) }
                    }
                }
            }
        }

        Ul({
            classes(optionList.block)
            id(listId)
            attr("role", "listbox")
            if (labelledBy != null) {
                attr("aria-labelledby", labelledBy)
            } else {
                attr("aria-label", label)
            }
            if (!expanded) {
                attr("hidden", "")
            }
        }) {
            when {
                options == null -> Li({ classes(optionList.element("message").className) }) { Text("Lade …") }
                matches.isEmpty() -> Li({ classes(optionList.element("message").className) }) { Text(noMatches) }
                else -> matches.forEachIndexed { index, option ->
                    Li({
                        classes(optionList.element("option").className)
                        id("$listId-$index")
                        attr("role", "option")
                        attr("aria-selected", (index == activeIndex).toString())
                        // keeps the focus in the field, so the list stays open for the next name
                        onMouseDown { it.preventDefault() }
                        onClick { add(option) }
                    }) { Text(option.label) }
                }
            }
        }
    }
}

/**
 * The person of a record, such as a board member's or an account's: a single [AdminTokenInput] in a field labelled
 * "Person", with [hint] or [error] below. [people] is `null` while they load; [fallbackName] names a chosen person who
 * is missing from them, such as the stored one. [loadError] says why loading failed, with [onRetry] to try again.
 * An [optional] field may stay empty.
 */
@Composable
fun AdminPersonPicker(
    id: String,
    personId: Long?,
    people: List<PersonListEntry>?,
    onChange: (Long?) -> Unit,
    hint: String,
    error: String? = null,
    fallbackName: String? = null,
    loadError: String? = null,
    onRetry: () -> Unit = {},
    optional: Boolean = false,
) {
    val labelId = "$id-label"
    val messageId = "$id-message"
    val failed = loadError != null && people == null
    val selected = listOfNotNull(
        personId?.let { chosenId ->
            val name = people?.firstOrNull { it.id == chosenId }?.name ?: fallbackName
            TokenOption(chosenId, name ?: if (people == null && !failed) "Lade …" else "Person $chosenId")
        },
    )

    Div({
        classes(field.block)
        attr("role", "group")
        attr("aria-labelledby", labelId)
    }) {
        Span({
            classes(field.element("label").className)
            id(labelId)
        }) {
            Text("Person")
            if (optional) {
                Text(" ")
                Span({ classes(field.element("optional").className) }) { Text("(optional)") }
            }
        }
        AdminTokenInput(
            id = id,
            label = "Person",
            selected = selected,
            // after a failed load the search stays disabled, without claiming to load
            options = if (failed) emptyList() else people?.map { TokenOption(it.id, it.name) },
            onAdd = { onChange(it) },
            onRemove = { onChange(null) },
            placeholder = "Person suchen …",
            noMatches = "Keine Person gefunden.",
            invalid = error != null,
            single = true,
            disabled = failed,
            labelledBy = labelId,
            describedBy = messageId,
        )
        Span({
            classes(field.element(if (error != null || loadError != null) "error" else "hint").className)
            id(messageId)
        }) {
            when {
                error != null -> Text(error)
                loadError != null -> Text("Die Personen konnten nicht geladen werden. $loadError")
                else -> Text(hint)
            }
        }
        if (loadError != null) {
            Div { AdminButton(variant = ButtonVariant.LINK, onClick = onRetry) { Text("Personen neu laden") } }
        }
    }
}
